import type { CSSProperties } from 'react';
import { formatFigure, phaseColor } from '@/lib/phase';
import type { DebugEvent } from '@/session/debug';

/**
 * One row per event on the shared column axis. The track spans the wait the event closed rather
 * than the event itself, so the long pole in a turn is the widest bar.
 */
export function Waterfall({ events }: { events: DebugEvent[] }) {
  return (
    <ol className="mt-3">
      {events.map((event, index) => (
        <Row key={`${event.description}-${index}`} event={event} />
      ))}
    </ol>
  );
}

function Row({ event }: { event: DebugEvent }) {
  const local = event.source === 'client';
  const hue = { '--c': phaseColor(event.phase) } as CSSProperties;

  return (
    <li className="dbg-event">
      <div className="dbg-row">
        <span className={local ? 'dbg-dot dbg-dot-local' : 'dbg-dot'} style={hue} />
        <span className={local ? 'dbg-desc dbg-desc-local' : 'dbg-desc'}>{event.description}</span>
        <span className="dbg-track">
          <i
            style={{
              ...hue,
              left: `${event.trackStart * 100}%`,
              width: `${event.trackWidth * 100}%`,
            }}
          />
        </span>
        <span className="dbg-delta">
          +{formatFigure(event.sincePrevMs)}
          <i className="dbg-unit">ms</i>
        </span>
        <span className="dbg-delta dbg-delta-cum">
          {formatFigure(event.sinceTurnMs)}
          <i className="dbg-unit">ms</i>
        </span>
      </div>

      {event.hasContent && (
        <details className="dbg-disclosure">
          <summary className="dbg-summary">
            <span className="dbg-chevron" aria-hidden="true" />
            <span className="dbg-preview">{event.preview}</span>
          </summary>
          <pre className="dbg-pre">{event.full}</pre>
        </details>
      )}
    </li>
  );
}
